import React, { useId, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Col, Button, Form } from 'reactstrap';
import { FormField } from 'site-modules/shared/components/form-field/form-field';
import { Spinner } from 'site-modules/shared/components/spinner/spinner';
import { Link } from 'site-modules/shared/components/link/link';

export function AppraisalReportStepExtendedEmailSection({
  validationErrors: { email: emailError } = {},
  validate,
  userEmail = '',
  isMobile = false,
  isSubmitting = false,
  isSubmitted = false,
  fieldRef,
  handleSubmitEmail,
  onEmailFormFieldFocus,
}) {
  const ctaText = isSubmitted ? 'Sent!' : 'SEND';
  const placeholderText = isMobile ? 'Email' : 'Enter Your Email';
  const uniqInputId = useId();
  const emailErrorId = `error-email-${uniqInputId}`;

  const inputRef = useCallback(
    field => {
      if (!field) {
        return undefined;
      }

      return fieldRef(field.props.name, field);
    },
    [fieldRef]
  );

  const isValid = !emailError;

  return (
    <div className="mb-2">
      <div className="rounded email-section-wrapper with-shadow pt-1_5 pb-1_5 w-100 px-1 px-md-1_5">
        <div className="text-start">
          <div className="d-flex align-items-center mb-0_5">
            <span className="icon-edmunds-car text-primary size-24 me-0_5" aria-hidden />
            <h3 className="heading-5 mb-0 d-inline">
              Save your <span className="text-blue-50">appraisal report</span>
            </h3>
          </div>
          <div className="size-14">
            Get a copy of your appraisal report and monthly updates on the value of your car.
          </div>
        </div>
        <Form noValidate onSubmit={handleSubmitEmail} className="mt-1">
          <Col xl={8} md={10} className="px-0">
            <div className="d-flex">
              <FormField
                className="flex-grow-1 mb-0"
                defaultValue={userEmail}
                data-tracking-id="provide_email"
                data-tracking-ignore-value
                id={`offer-card-email-${uniqInputId}`}
                maxLength={255}
                name="email"
                type="email"
                autoComplete="email"
                inputClassName="mb-0 email-input h-100"
                aria-label={placeholderText}
                aria-describedby={emailErrorId}
                ref={inputRef}
                isValid={isValid}
                ariaInvalid={!isValid}
                required
                onFocus={onEmailFormFieldFocus}
                onBlur={validate}
                readOnly={isSubmitting}
                aria-required
                label={<Fragment>Email &dagger;</Fragment>}
                floatingPosition="on-border"
                useFloatingLabel
              />
              <Button
                type="submit"
                color="success"
                className={classNames('size-16 text-white py-0_5 track-btn text-capitalize px-0_5 px-md-1', {
                  'ps-0_75': !isMobile && isSubmitting,
                  'ps-0_25': isMobile && isSubmitting,
                })}
                data-tracking-id="submit_sign_up_email"
                disabled={isSubmitting}
              >
                {isSubmitting && (
                  <span className={classNames({ 'pe-0_5': !isMobile, 'pe-0_25': isMobile })}>
                    <Spinner size={14} thickness={1} color="white" />
                  </span>
                )}
                <span className="size-16">{ctaText}</span>
                <span className="visually-hidden" role="status">
                  {isSubmitted ? 'Email sent' : ''}
                  {isSubmitting ? 'Sending email' : ''}
                </span>
              </Button>
            </div>
            {emailError && (
              <div id={emailErrorId} className="size-12 mt-0_25 text-danger d-none d-md-block">
                {emailError}
              </div>
            )}
          </Col>
        </Form>
      </div>
      <div className={classNames('text-cool-gray-50 size-10', { 'mt-0_25': !isMobile, 'mt-0_75': isMobile })}>
        &dagger; By clicking Send, I accept the terms of the{' '}
        <Link
          target="_blank"
          rel="noopener noreferrer"
          to="/about/visitor-agreement.html"
          className="text-gray-darker disclaimer"
        >
          <u>Edmunds Visitor Agreement</u>
        </Link>
        .{' '}
        <Link
          target="_blank"
          rel="noopener noreferrer"
          to="/about/privacy.html"
          className="text-gray-darker disclaimer"
        >
          <u>Our collection and use of your personal information</u>
        </Link>
        .
      </div>
    </div>
  );
}

AppraisalReportStepExtendedEmailSection.propTypes = {
  validationErrors: PropTypes.objectOf(PropTypes.string),
  validate: PropTypes.func.isRequired,
  userEmail: PropTypes.string,
  isMobile: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  isSubmitted: PropTypes.bool,
  fieldRef: PropTypes.func.isRequired,
  handleSubmitEmail: PropTypes.func.isRequired,
  onEmailFormFieldFocus: PropTypes.func.isRequired,
};
